import {
  buildBroadLocationText,
  normalizeBroadLocation,
  parseBroadTypedLocation,
  looksLikeStreetAddress,
} from "./locationArea";

const STATE_ALIASES = {
  al: "alabama",
  ak: "alaska",
  az: "arizona",
  ar: "arkansas",
  ca: "california",
  co: "colorado",
  ct: "connecticut",
  de: "delaware",
  fl: "florida",
  ga: "georgia",
  hi: "hawaii",
  id: "idaho",
  il: "illinois",
  in: "indiana",
  ia: "iowa",
  ks: "kansas",
  ky: "kentucky",
  la: "louisiana",
  me: "maine",
  md: "maryland",
  ma: "massachusetts",
  mi: "michigan",
  mn: "minnesota",
  ms: "mississippi",
  mo: "missouri",
  mt: "montana",
  ne: "nebraska",
  nv: "nevada",
  nh: "new hampshire",
  nj: "new jersey",
  nm: "new mexico",
  ny: "new york",
  nc: "north carolina",
  nd: "north dakota",
  oh: "ohio",
  ok: "oklahoma",
  or: "oregon",
  pa: "pennsylvania",
  ri: "rhode island",
  sc: "south carolina",
  sd: "south dakota",
  tn: "tennessee",
  tx: "texas",
  ut: "utah",
  vt: "vermont",
  va: "virginia",
  wa: "washington",
  wv: "west virginia",
  wi: "wisconsin",
  wy: "wyoming",
};

const normalizeText = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeState = (value = "") => {
  const normalized = normalizeText(value);
  return STATE_ALIASES[normalized] || normalized;
};

const normalizePostalCode = (value = "") =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

const STATE_NAMES = new Set(Object.values(STATE_ALIASES));

const normalizeAddressQuery = (value = "") =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const ZIP_PATTERN = /\b\d{5}(?:-\d{4})?\b/;

const parseExactUsAddressQuery = (query = "") => {
  const parts = String(query)
    .split(",")
    .map(normalizeAddressQuery)
    .filter(Boolean);
  const streetIndex = parts.findIndex((part) => looksLikeStreetAddress(part));
  if (streetIndex < 0) return null;

  const street = parts[streetIndex];
  const tailParts = parts.slice(streetIndex + 1);
  const tailText = tailParts.join(" ");
  const postalCode = tailText.match(ZIP_PATTERN)?.[0] || "";
  const state = tailParts
    .slice(1)
    .join(" ")
    .replace(postalCode, "")
    .replace(/\b(usa|united states)\b/gi, "")
    .replace(/[,\s]+/g, " ")
    .trim();

  return {
    street,
    city: tailParts[0] || "",
    state,
    postalCode,
  };
};

export const buildExactGeocodeQueries = (query) => {
  const rawQuery = normalizeAddressQuery(query);
  if (!rawQuery) return [];

  const parsed = parseExactUsAddressQuery(rawQuery);
  const queries = [rawQuery];

  if (parsed?.street && (parsed.state || parsed.postalCode)) {
    const statePostal = [parsed.state, parsed.postalCode].filter(Boolean).join(" ");
    queries.push([parsed.street, statePostal, "USA"].filter(Boolean).join(", "));
    if (parsed.postalCode) {
      queries.push([parsed.street, parsed.postalCode, "USA"].filter(Boolean).join(", "));
    }
  }

  if (!/\b(usa|united states)\b/i.test(rawQuery)) {
    queries.push(`${rawQuery}, USA`);
  }

  return [...new Set(queries.filter(Boolean))];
};

const parseInputLocation = (query) => {
  if (query && typeof query === "object") {
    return normalizeBroadLocation(query, query.locationText || "");
  }

  return parseBroadTypedLocation(query);
};

const knownLocationFallback = (parsed = {}) => {
  const city = normalizeText(parsed.city);
  const county = normalizeText(parsed.county);
  const state = normalizeState(parsed.state);

  if (state !== "tennessee") return null;

  if (city === "normandy") {
    return {
      lat: 35.4523,
      lng: -86.2605,
      displayName: "Normandy, Bedford County, Tennessee, United States",
      query: "known-location-fallback:normandy-tn",
    };
  }

  if (county === "bedford county" || county === "bedford") {
    return {
      lat: 35.5099479,
      lng: -86.4507189,
      displayName: "Bedford County, Tennessee, United States",
      query: "known-location-fallback:bedford-county-tn",
    };
  }

  return null;
};

export const buildGeocodeQueries = (query) => {
  const rawQuery =
    typeof query === "string"
      ? query.trim()
      : buildBroadLocationText(query || {}) || query?.locationText || "";
  const parsed = parseInputLocation(query);
  const queries = [
    parsed.city && parsed.state ? `${parsed.city}, ${parsed.state}` : "",
    parsed.city && parsed.county && parsed.state
      ? `${parsed.city}, ${parsed.county}, ${parsed.state}`
      : "",
    parsed.county && parsed.state ? `${parsed.county}, ${parsed.state}` : "",
    rawQuery,
  ];

  return [...new Set(queries.map((item) => item.trim()).filter(Boolean))];
};

const getResultAddress = (result = {}) => result.address || {};

const resultLocality = (address = {}) =>
  address.city ||
  address.town ||
  address.village ||
  address.hamlet ||
  address.municipality ||
  address.locality ||
  "";

const getGoogleAddressComponent = (components = [], type = "") =>
  components.find((component) => Array.isArray(component.types) && component.types.includes(type)) || null;

const getDisplayAddressParts = (result = {}) => {
  const displayName =
    result.displayName ||
    result.formattedAddress ||
    result.formatted_address ||
    result.display_name ||
    "";
  const tokens = String(displayName)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return {
    country: tokens.find((part) => {
      const normalized = normalizeText(part);
      return normalized === "us" || normalized === "usa" || normalized === "united states";
    }) || "",
    state: tokens.find((part) => STATE_NAMES.has(normalizeState(part))) || "",
    postalCode: String(displayName).match(ZIP_PATTERN)?.[0] || "",
    locality: "",
  };
};

export const getGeocodeResultAddressParts = (result = {}) => {
  const displayParts = getDisplayAddressParts(result);
  const components = Array.isArray(result.addressComponents)
    ? result.addressComponents
    : Array.isArray(result.address_components)
      ? result.address_components
      : [];

  if (components.length > 0) {
    const country = getGoogleAddressComponent(components, "country");
    const state = getGoogleAddressComponent(components, "administrative_area_level_1");
    const postalCode = getGoogleAddressComponent(components, "postal_code");
    const locality =
      getGoogleAddressComponent(components, "locality") ||
      getGoogleAddressComponent(components, "postal_town") ||
      getGoogleAddressComponent(components, "sublocality") ||
      getGoogleAddressComponent(components, "administrative_area_level_2");

    return {
      country: country?.short_name || country?.long_name || displayParts.country,
      state: state?.short_name || state?.long_name || displayParts.state,
      postalCode: postalCode?.long_name || postalCode?.short_name || displayParts.postalCode,
      locality: locality?.long_name || locality?.short_name || "",
    };
  }

  const address = getResultAddress(result);
  return {
    country: address.country_code || address.country || displayParts.country,
    state: address.state || displayParts.state,
    postalCode: address.postcode || address.postal_code || displayParts.postalCode,
    locality: resultLocality(address),
  };
};

export const validateExactUsGeocodeResult = (result = {}, input = {}) => {
  const enteredState = normalizeState(input.state);
  const enteredZip = normalizePostalCode(input.zip);
  if (!enteredState || !enteredZip) {
    return { valid: true, parts: getGeocodeResultAddressParts(result) };
  }

  const parts = getGeocodeResultAddressParts(result);
  const returnedCountry = normalizeText(parts.country);
  const returnedState = normalizeState(parts.state);
  const returnedZip = normalizePostalCode(parts.postalCode);

  if (result.partialMatch === true || result.partial_match === true) {
    return { valid: false, reason: "partial_match", parts };
  }

  if (returnedCountry && returnedCountry !== "us" && returnedCountry !== "united states" && returnedCountry !== "usa") {
    return { valid: false, reason: "country_mismatch", parts };
  }

  if (returnedState && returnedState !== enteredState) {
    return { valid: false, reason: "state_mismatch", parts };
  }

  if (returnedZip && returnedZip !== enteredZip) {
    return { valid: false, reason: "zip_mismatch", parts };
  }

  return { valid: true, parts };
};

export const getValidatedExactUsGeocodeResult = (result = null, input = {}) => {
  if (!result || !Number.isFinite(result.lat) || !Number.isFinite(result.lng)) {
    return { valid: false, reason: "no_result", parts: {} };
  }

  const validation = validateExactUsGeocodeResult(result, input);
  if (!validation.valid) return validation;

  return {
    ...validation,
    lat: result.lat,
    lng: result.lng,
    displayName: result.displayName || "",
  };
};

const getGoogleLatLngValue = (value) => {
  if (typeof value === "function") return Number(value());
  return Number(value);
};

const normalizeGoogleRestriction = (value = "") => String(value || "").trim();

export const buildGoogleMapsGeocodeRequest = (address, input = {}) => {
  const componentRestrictions = {
    country: "US",
  };
  const state = normalizeGoogleRestriction(input.state);
  const postalCode = normalizeGoogleRestriction(input.zip);

  if (state) componentRestrictions.administrativeArea = state;
  if (postalCode) componentRestrictions.postalCode = postalCode;

  return {
    address: normalizeAddressQuery(address),
    componentRestrictions,
  };
};

export const geocodeExactLocationWithGoogleMaps = async (address, input = {}, maps = null) => {
  const googleMaps = maps || (typeof window !== "undefined" ? window.google?.maps : null);
  if (!googleMaps?.Geocoder) return null;

  const request = buildGoogleMapsGeocodeRequest(address, input);
  if (!request.address) return null;

  const geocoder = new googleMaps.Geocoder();
  const okStatus = googleMaps.GeocoderStatus?.OK || "OK";
  const response = await new Promise((resolve, reject) => {
    let settled = false;
    const finish = (results, status) => {
      if (settled) return;
      settled = true;
      resolve({ results: Array.isArray(results) ? results : [], status });
    };

    try {
      const maybePromise = geocoder.geocode(request, (results, status) => {
        finish(results, status);
      });

      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise
          .then((promiseResponse) => {
            finish(promiseResponse?.results, promiseResponse?.status || okStatus);
          })
          .catch(reject);
      }
    } catch (error) {
      reject(error);
    }
  });

  if (response.status !== okStatus || !response.results?.[0]) return null;

  const result = response.results[0];
  const location = result.geometry?.location || {};
  const lat = getGoogleLatLngValue(location.lat);
  const lng = getGoogleLatLngValue(location.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    lat,
    lng,
    displayName: result.formatted_address || "",
    formattedAddress: result.formatted_address || "",
    addressComponents: result.address_components || [],
    partialMatch: result.partial_match === true,
    types: result.types || [],
    locationType: result.geometry?.location_type || "",
    query: request.address,
  };
};

const resultScore = (result, parsed) => {
  const lat = Number.parseFloat(result?.lat);
  const lng = Number.parseFloat(result?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return -1;

  const address = getResultAddress(result);
  let score = 1;

  if (normalizeText(address.country_code) === "us") score += 4;
  if (parsed.state && normalizeState(address.state) === normalizeState(parsed.state)) {
    score += 4;
  }
  if (parsed.county && normalizeText(address.county) === normalizeText(parsed.county)) {
    score += 2;
  }
  if (parsed.city && normalizeText(resultLocality(address)) === normalizeText(parsed.city)) {
    score += 3;
  }
  if (result.type && ["city", "town", "village", "hamlet", "administrative"].includes(result.type)) {
    score += 1;
  }

  return score;
};

const bestGeocodeResult = (results = [], parsed) =>
  results
    .map((result) => ({ result, score: resultScore(result, parsed) }))
    .filter((item) => item.score >= 0)
    .sort((a, b) => b.score - a.score)[0]?.result || null;

const bestExactGeocodeResult = (results = [], parsed) =>
  results
    .map((result) => ({ result, score: resultScore(result, parsed) }))
    .filter(({ result, score }) => {
      const address = getResultAddress(result);
      return (
        score >= 0 &&
        (address.house_number || address.road || result.type === "address")
      );
    })
    .sort((a, b) => {
      const aAddress = getResultAddress(a.result);
      const bAddress = getResultAddress(b.result);
      const aHasHouseNumber = Boolean(aAddress.house_number);
      const bHasHouseNumber = Boolean(bAddress.house_number);
      if (aHasHouseNumber !== bHasHouseNumber) return bHasHouseNumber - aHasHouseNumber;
      return b.score - a.score;
    })[0]?.result || null;

export const geocodeLocation = async (query) => {
  const parsed = parseInputLocation(query);

  try {
    for (const geocodeQuery of buildGeocodeQueries(query)) {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&addressdetails=1&countrycodes=us&q=${encodeURIComponent(geocodeQuery)}`;
      const res = await fetch(url);
      const data = await res.json();
      const result = bestGeocodeResult(data, parsed);

      if (result) {
        return {
          lat: Number.parseFloat(result.lat),
          lng: Number.parseFloat(result.lon),
          displayName: result.display_name,
          query: geocodeQuery,
          address: result.address || {},
          addressComponents: result.address_components || [],
          partialMatch: result.partial_match === true,
        };
      }
    }

    return knownLocationFallback(parsed);
  } catch (err) {
    console.error("Geocode failed:", err);
    return knownLocationFallback(parsed);
  }
};

// Event locations may include a real street address. Keep that exact address
// first so map focus does not degrade to a city-center or viewer-location pin.
export const geocodeExactLocation = async (query) => {
  const rawQuery = normalizeAddressQuery(query);
  if (!rawQuery) return null;

  try {
    for (const geocodeQuery of buildExactGeocodeQueries(rawQuery)) {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&addressdetails=1&countrycodes=us&q=${encodeURIComponent(geocodeQuery)}`;
      const res = await fetch(url);
      const data = await res.json();
      const result =
        bestExactGeocodeResult(data, parseInputLocation(geocodeQuery)) ||
        bestGeocodeResult(data, parseInputLocation(geocodeQuery));

      if (result) {
        return {
          lat: Number.parseFloat(result.lat),
          lng: Number.parseFloat(result.lon),
          displayName: result.display_name,
          query: geocodeQuery,
          address: result.address || {},
          addressComponents: result.address_components || [],
          partialMatch: result.partial_match === true,
          type: result.type || "",
          category: result.category || "",
        };
      }
    }

    return null;
  } catch (err) {
    console.error("Exact geocode failed:", err);
    return null;
  }
};

export const reverseGeocode = async (lat, lng) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
    const res = await fetch(url);
    const data = await res.json();

    if (data && data.address) {
      const city =
        data.address.city ||
        data.address.town ||
        data.address.village ||
        "";
      const state = data.address.state || "";
      const county =
        data.address.county ||
        data.address.state_district ||
        data.address.region ||
        "";

      return {
        city,
        state,
        county,
        locationText: buildBroadLocationText({ city, state, county }),
      };
    }

    return null;
  } catch (err) {
    console.error("Reverse geocode failed:", err);
    return null;
  }
};
